import React, { useEffect } from 'react';
import { BASE_URL } from '../data/pageInfo';
import { CONTACT } from '../data/contactInfo';

/**
 * Composant pour injecter des données structurées JSON-LD pour le SEO
 * Améliore le référencement et l'affichage dans les résultats de recherche
 */

// Helper pour injecter le script JSON-LD
function injectJSONLD(id, schema) {
  // Supprimer l'ancien script s'il existe
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  // Créer et injecter le nouveau script
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);

  return () => {
    const el = document.getElementById(id);
    if (el) {
      el.remove();
    }
  };
}

export function OrganizationSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Tanguy Perroit Photographie",
    "alternateName": "Tanguy Perroit - Photographe Professionnel",
    "url": BASE_URL,
    "logo": `${BASE_URL}/logo192.png`,
    "image": `${BASE_URL}/logo512.png`,
    "description": "Photographe professionnel à Tours spécialisé en portraits, reportages corporate et événements. Plus de 8 ans d'expérience et 50+ séries photos réalisées.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": CONTACT.location.city,
      "postalCode": CONTACT.location.postalCode,
      "addressRegion": CONTACT.location.region,
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "47.394144",
      "longitude": "0.68484"
    },
    "telephone": CONTACT.phone.international,
    "email": CONTACT.email,
    "priceRange": "€€",
    "openingHours": "Mo-Fr 09:00-18:00",
    "sameAs": [
      CONTACT.social.instagram,
      CONTACT.social.linkedin
    ],
    "serviceType": [
      "Photographie de Portrait",
      "Photographie Corporate",
      "Photographie Événementielle",
      "Photographie de Mariage"
    ]
  };

  useEffect(() => {
    return injectJSONLD('organization-schema', schema);
  }, []);

  return null;
}

export function PersonSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Tanguy Perroit",
    "jobTitle": "Photographe Professionnel",
    "url": BASE_URL,
    "image": `${BASE_URL}/logo512.png`,
    "description": "Photographe professionnel basé à Tours avec plus de 8 ans d'expérience",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": CONTACT.location.city,
      "postalCode": CONTACT.location.postalCode,
      "addressCountry": "FR"
    },
    "knowsAbout": [
      "Photographie",
      "Portrait",
      "Événementiel",
      "Corporate",
      "Reportage"
    ]
  };

  useEffect(() => {
    return injectJSONLD('person-schema', schema);
  }, []);

  return null;
}

export function ServiceSchema({ serviceType, name, description, price }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceType,
    "name": name,
    "description": description,
    "provider": {
      "@type": "ProfessionalService",
      "name": "Tanguy Perroit Photographie",
      "url": BASE_URL
    },
    "areaServed": {
      "@type": "City",
      "name": "Tours"
    },
    "offers": {
      "@type": "Offer",
      "price": price || "Sur devis",
      "priceCurrency": "EUR"
    }
  };

  useEffect(() => {
    return injectJSONLD('service-schema', schema);
  }, [serviceType, name, description, price]);

  return null;
}

export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${BASE_URL}${item.url}`
    }))
  };

  useEffect(() => {
    return injectJSONLD('breadcrumb-schema', schema);
  }, [items]);

  return null;
}

export function ImageObjectSchema({ images, title, description }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": title,
    "description": description,
    "image": images.map(img => ({
      "@type": "ImageObject",
      "url": img.url,
      "caption": img.caption || title,
      "author": {
        "@type": "Person",
        "name": "Tanguy Perroit"
      },
      "copyrightHolder": {
        "@type": "Person",
        "name": "Tanguy Perroit"
      }
    }))
  };

  useEffect(() => {
    return injectJSONLD('imageobject-schema', schema);
  }, [images, title, description]);

  return null;
}

export function WebPageSchema({ title, description, url }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": url,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Tanguy Perroit Photographie",
      "url": BASE_URL
    },
    "author": {
      "@type": "Person",
      "name": "Tanguy Perroit"
    }
  };

  useEffect(() => {
    return injectJSONLD('webpage-schema', schema);
  }, [title, description, url]);

  return null;
}

export function FAQSchema({ questions }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };

  useEffect(() => {
    return injectJSONLD('faq-schema', schema);
  }, [questions]);

  return null;
}
